import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Divider,
  CircularProgress,
  MenuItem,
  Grid,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { getUTCDateFromParts } from "../../utils/dates/getUTCDateFromParts";
import api from "../../services/api";

const UpdateInfoPage = () => {
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dob, setDob] = useState({ day: "", month: "", year: "" });

  const [firstNameError, setFirstNameError] = useState(false);
  const [lastNameError, setLastNameError] = useState(false);
  const [dobError, setDobError] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    const hasFirstName = !!firstName.trim();
    const hasLastName = !!lastName.trim();
    const hasDob = dob.day && dob.month && dob.year;

    setFirstNameError(!hasFirstName);
    setLastNameError(!hasLastName);
    setDobError(!hasDob);

    if (!hasFirstName || !hasLastName || !hasDob) return;

    setLoading(true);

    try {
      const dobUtc = getUTCDateFromParts({
        year: parseInt(dob.year),
        month: parseInt(dob.month),
        day: parseInt(dob.day),
      });

      await api.patch("/users/me", {
        firstName,
        lastName,
        dob: dobUtc,
        firstVisitAfterCrash: false,
      });

      navigate("/");
    } catch (err) {
      console.error("Failed to update info", err);
      alert("Something went wrong. Please contact support.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#fd776220",
      }}>
      <Paper elevation={6} sx={{ p: 4, width: "90%", maxWidth: 500 }}>
        <Typography
          variant="h5"
          sx={{ color: "#FD7762", fontWeight: 600, mb: 1 }}>
          SECURITY ENHANCEMENT
        </Typography>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          We’re enhancing our systems — help us verify your info.
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          To continue using your account, please confirm your details below.
        </Typography>

        <TextField
          fullWidth
          label="First Name"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          error={firstNameError}
          helperText={firstNameError && "First name is required"}
          sx={{ mt: 3 }}
        />

        <TextField
          fullWidth
          label="Last Name"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          error={lastNameError}
          helperText={lastNameError && "Last name is required"}
          sx={{ mt: 2 }}
        />

        <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
          Date of Birth
        </Typography>
        <Grid container spacing={1}>
          <Grid item xs={4}>
            <TextField
              select
              label="Day"
              value={dob.day}
              onChange={(e) =>
                setDob((prev) => ({ ...prev, day: e.target.value }))
              }
              fullWidth
              error={dobError && !dob.day}>
              {[...Array(31).keys()].map((d) => (
                <MenuItem key={d + 1} value={String(d + 1).padStart(2, "0")}>
                  {d + 1}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={4}>
            <TextField
              select
              label="Month"
              value={dob.month}
              onChange={(e) =>
                setDob((prev) => ({ ...prev, month: e.target.value }))
              }
              fullWidth
              error={dobError && !dob.month}>
              {[...Array(12).keys()].map((i) => (
                <MenuItem key={i + 1} value={String(i + 1).padStart(2, "0")}>
                  {new Date(0, i).toLocaleString("default", { month: "short" })}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={4}>
            <TextField
              select
              label="Year"
              value={dob.year}
              onChange={(e) =>
                setDob((prev) => ({ ...prev, year: e.target.value }))
              }
              fullWidth
              error={dobError && !dob.year}>
              {Array.from({ length: 100 }, (_, i) => {
                const year = new Date().getFullYear() - i;
                return (
                  <MenuItem key={year} value={String(year)}>
                    {year}
                  </MenuItem>
                );
              })}
            </TextField>
          </Grid>
        </Grid>
        {dobError && (
          <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
            Please complete your date of birth
          </Typography>
        )}

        <Box sx={{ position: "relative", mt: 4 }}>
          <Button
            fullWidth
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            sx={{ py: 1.25 }}>
            Submit
          </Button>
          {loading && (
            <CircularProgress
              size={24}
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                marginTop: "-12px",
                marginLeft: "-12px",
              }}
            />
          )}
        </Box>

        <Divider sx={{ my: 4 }} />

        <Typography variant="body2" align="center" color="text.secondary">
          CAKEYHAND DESSERT FACTORY INC. © {new Date().getFullYear()} All rights
          reserved
        </Typography>
      </Paper>
    </Box>
  );
};

export default UpdateInfoPage;
